import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// Mounted only inside authenticated Admin. Existing request handlers keep their
// success/error behavior; a guarded write waits for an explicit editorial choice.
export function AdminEditorialReview() {
  const [queue, setQueue] = useState([]);
  const pending = useRef(new Set());
  const sequence = useRef(0);
  useEffect(() => {
    const interceptor = api.interceptors.response.use((response) => response, (error) => {
      const detail = error.response?.data?.detail;
      const config = error.config;
      if (error.response?.status !== 409 || detail?.editorial_review !== true
        || !config || !/\/admin\//.test(config.url || "")
        || !["post", "put", "patch"].includes((config.method || "").toLowerCase())) {
        return Promise.reject(error);
      }
      return new Promise((resolve, reject) => {
        const item = { error, detail, config, resolve, reject, key: ++sequence.current };
        pending.current.add(item);
        setQueue((items) => [...items, item]);
      });
    });
    return () => {
      api.interceptors.response.eject(interceptor);
      for (const item of pending.current) item.reject(new Error("Editorial review closed; action was not retried."));
      pending.current.clear();
    };
  }, []);

  const finish = (item, decision) => {
    pending.current.delete(item);
    setQueue((items) => items.filter((entry) => entry !== item));
    if (!decision) {
      item.reject(new Error("Editorial review cancelled; nothing was saved."));
      return;
    }
    try {
      const body = typeof item.config.data === "string" ? JSON.parse(item.config.data) : item.config.data;
      if (!body || typeof body !== "object" || Array.isArray(body)) throw new Error("Invalid editorial request");
      item.resolve(api.request({ ...item.config, data: { ...body, _editorial_review: {
        content_hash: item.detail.content_hash, puzzle_id: item.detail.puzzle_id, generation: item.detail.generation,
        warning_ids: decision.ids, note: decision.note,
      } } }));
    } catch (error) { item.reject(error); }
  };
  if (!queue.length) return null;
  return <EditorialDecision key={queue[0].key} item={queue[0]} onFinish={finish} />;
}

function EditorialDecision({ item, onFinish }) {
  const [checked, setChecked] = useState(new Set());
  const [note, setNote] = useState("");
  const warnings = item.detail.warnings || [];
  const ready = warnings.length > 0 && warnings.every((w) => checked.has(w.warning_id)) && note.trim().length >= 5 && note.length <= 1000;
  return (
    <div className="fixed inset-0 z-[150] bg-black/80 p-4 flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="editorial-review-title" data-testid="editorial-review">
      <div className="bg-card border-2 border-primary p-5 w-full max-w-3xl max-h-[92vh] overflow-auto">
        <h2 id="editorial-review-title" className="font-display text-2xl">Editorial Review Required</h2>
        <p className="text-sm text-muted-foreground my-3">{item.detail.message} Confirmed duplicates cannot be approved automatically. Possible similarities require your judgment.</p>
        <div className="space-y-4">
          {warnings.map((warning) => (
            <section key={warning.warning_id} className="border border-white/20 p-3" data-testid="editorial-warning">
              <p className="font-bold">{warning.answer}</p>
              {warning.clue && <p className="text-sm">{warning.clue}</p>}
              <p className="text-xs text-primary my-2">{warning.confidence} · {warning.explanation}</p>
              {(warning.previous || []).map((previous, index) => (
                <blockquote key={index} className="border-l-2 border-white/20 pl-3 my-2 text-sm">
                  <p><strong>{previous.answer}</strong> — {previous.clue}</p>
                  <p className="text-xs text-muted-foreground">{previous.puzzle_title || "Word Bank"} · {previous.puzzle_id || previous.entry_id || "recorded history"}{previous.dates?.length ? ` · ${previous.dates.join(", ")}` : ""}</p>
                </blockquote>
              ))}
              <label className="flex items-center gap-2 text-sm mt-3">
                <input type="checkbox" checked={checked.has(warning.warning_id)} onChange={(event) => setChecked((old) => { const next = new Set(old); if (event.target.checked) next.add(warning.warning_id); else next.delete(warning.warning_id); return next; })} />
                I reviewed this warning and approve a legitimate exception.
              </label>
            </section>
          ))}
        </div>
        <label className="block text-sm mt-4" htmlFor="editorial-reason">Reason for the exception (saved with the approval)</label>
        <textarea id="editorial-reason" value={note} maxLength={1000} onChange={(event) => setNote(event.target.value)} rows={3} className="w-full bg-background border border-border p-2 mt-2" />
        <div className="flex flex-wrap gap-3 mt-4">
          <button disabled={!ready} onClick={() => onFinish(item, { ids: [...checked], note: note.trim() })} className="bg-primary text-primary-foreground px-4 py-3 font-mono-caps text-xs disabled:opacity-40" data-testid="editorial-approve-exception">Approve exceptions and retry</button>
          <button onClick={() => onFinish(item, null)} className="border border-white/20 px-4 py-3 font-mono-caps text-xs">Cancel — return to edit</button>
        </div>
      </div>
    </div>
  );
}
